/**
 * トレーニングモード — Frontend ローカル Contract
 *
 * backend（feat/training-mode-backend）で実装予定の training 系 5 エンドポイントを、
 * `@standandforce/api-contract` の publish 前の暫定としてローカルに定義する。
 * スキーマは設計合意（backend トレーニングモード 実装設計 確定版 2026-07-13）と一致させる。
 *
 * TODO(training): backend 実装が publish されたら本ファイルを削除し、client.ts の import を
 *   `@standandforce/api-contract` に切り替える（エクスポート名は backend 側と要すり合わせ）。
 */
import { oc } from '@orpc/contract';
import { SoftwareIntegrationGetSettingOutputSchema } from '@standandforce/api-contract';
import { z } from 'zod';

// TODO(training-20260716): api-contract publish 後に生成型へ差し替え
/** 設定 GET / training 系 mutation が返す training セクション。 */
export const TrainingSettingPartSchema = z.object({
  // AppTraining 行の有無（= トレーニングモード ON）
  enabled: z.boolean(),
  // ユーザー一覧レシピ設定済みか
  recipeConfigured: z.boolean(),
  recipeConfiguredAt: z.string().nullable(),
  // 認証変数は GET のみ解決される。password の value は backend が null にする。
  authVariables: z.array(z.object({
    name: z.string(),
    value: z.string().nullable(),
    isPassword: z.boolean()
  })).nullable()
})
// L1 マージ前の旧 UI が参照する未知フィールドを unknown としてのみ受理する。
// 新 contract の既知フィールドには含めず、L1 マージ後に catchall 自体を除去する。
.catchall(z.unknown());

/**
 * 設定 GET output の training 拡張。
 *
 * `training` は backend デプロイ前のレスポンスには存在しないため `.optional()` とする
 * （未デプロイ環境では UI 非表示に degrade する）。publish 版で required になっても
 * `?.` アクセスのまま互換。
 */
export const SoftwareIntegrationGetSettingWithTrainingOutputSchema = SoftwareIntegrationGetSettingOutputSchema.extend({
  training: TrainingSettingPartSchema.optional()
});
export const GetSoftwareIntegrationSettingsWithTrainingInputSchema = z.object({
  softwareId: z.string()
});

/**
 * 既存 GET `/v2/software/{softwareId}/integration/settings` の training 拡張版。
 * client.ts で package 版 `integrationSettings` route をこの route で差し替える。
 */
export const getSoftwareIntegrationSettingsWithTrainingRoute = oc.route({
  method: 'GET',
  path: '/v2/software/{softwareId}/integration/settings'
}).input(GetSoftwareIntegrationSettingsWithTrainingInputSchema).output(SoftwareIntegrationGetSettingWithTrainingOutputSchema);
export const PostSoftwareTrainingStartInputSchema = z.object({
  softwareId: z.string()
});

/**
 * レシピ設定開始の応答。`token` は training-server 認証用の ES256 短命 JWT で、
 * FE が拡張機能へ格納する（backend への直接認証は拡張からは行わない）。
 * `trainingServerUrl` は stage path 込みの training-server URL で、拡張機能の
 * apiBase になる。
 * `appName` / `organizationName` / `userName` は拡張機能の表示・コントロール用。
 */
export const PostSoftwareTrainingStartOutputSchema = z.object({
  token: z.string(),
  trainingServerUrl: z.string(),
  appName: z.string(),
  organizationName: z.string(),
  userName: z.string(),
  expiresAt: z.string()
});

/**
 * レシピ設定完了の入力。`recipe` は拡張機能から吸い出したレシピ JSON
 * （secrets 内包、training-server POST /recipes ボディと同形）。
 * 中身の検証は training-server 側（validateRecipe/validateSecrets）に一元化するため
 * FE では構造検証しない。
 */
export const PostSoftwareTrainingFinishInputSchema = z.object({
  softwareId: z.string(),
  recipe: z.record(z.string(), z.unknown())
});
export const DeleteSoftwareTrainingRecipeInputSchema = z.object({
  softwareId: z.string()
});

/**
 * training 系 mutation contract。応答はいずれも設定 GET と同 schema
 * （既存 integration/settings 系の update-features と同型）。
 */
export type TrainingSettingPart = z.infer<typeof TrainingSettingPartSchema>;
export type SoftwareIntegrationGetSettingWithTrainingOutput = z.infer<typeof SoftwareIntegrationGetSettingWithTrainingOutputSchema>;
export type PostSoftwareTrainingStartOutput = z.infer<typeof PostSoftwareTrainingStartOutputSchema>;
export const softwareTrainingContract = oc.router({
  start: oc.route({
    method: 'POST',
    path: '/v2/software/{softwareId}/integration/training/start'
  }).input(PostSoftwareTrainingStartInputSchema).output(PostSoftwareTrainingStartOutputSchema),
  finish: oc.route({
    method: 'POST',
    path: '/v2/software/{softwareId}/integration/training/finish'
  }).input(PostSoftwareTrainingFinishInputSchema).output(SoftwareIntegrationGetSettingWithTrainingOutputSchema),
  deleteRecipe: oc.route({
    method: 'DELETE',
    path: '/v2/software/{softwareId}/integration/training/recipe'
  }).input(DeleteSoftwareTrainingRecipeInputSchema).output(SoftwareIntegrationGetSettingWithTrainingOutputSchema)
});